import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { UserPlus, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  email: '', password: '', first_name: '', last_name: '',
  certification: '', license_number: '', role: 'provider'
};

export function UserManagementTab() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_FORM });

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setFetchError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/users`);
      setUsers(data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      console.error('Failed to fetch users:', status, detail);
      setFetchError(status === 401 ? 'Session expired. Please log in again.' : `Failed to load users: ${detail}`);
      toast.error(status === 401 ? 'Session expired — please log in again' : 'Failed to fetch users');
    } finally { setLoadingUsers(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const resetForm = () => { setEditingUser(null); setForm({ ...INITIAL_FORM }); };

  const handleEdit = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email, password: '', first_name: u.first_name, last_name: u.last_name,
      certification: u.certification || '', license_number: u.license_number || '', role: u.role
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, {
          first_name: form.first_name, last_name: form.last_name, email: form.email,
          certification: form.certification, license_number: form.license_number, role: form.role
        });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_URL}/api/users`, form);
        toast.success('User created successfully');
      }
      setShowDialog(false); resetForm(); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save user'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success('User deleted successfully'); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
  };

  const getRoleBadge = (role) => {
    const map = { admin: 'bg-purple-100 text-purple-700', provider: 'bg-blue-100 text-blue-700', qa_qi: 'bg-green-100 text-green-700' };
    return map[role] || 'bg-slate-100 text-slate-600';
  };

  const getLicenseLabel = (cert) => {
    if (cert === 'EMT' || cert === 'Paramedic') return 'EMT/Paramedic State Number *';
    if (cert === 'Nurse') return 'NYS Nursing License Number *';
    if (cert === 'PA') return 'PA License Number *';
    return 'MD License Number *';
  };

  const getLicensePlaceholder = (cert) => {
    if (cert === 'EMT' || cert === 'Paramedic') return 'Enter state certification number';
    if (cert === 'Nurse') return 'Enter NYS nursing license number';
    if (cert === 'PA') return 'Enter PA license number';
    return 'Enter MD license number';
  };

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>{users.length} total users</CardDescription>
          </div>
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-user-button">
            <UserPlus size={18} className="mr-2" /> Add New User
          </Button>
        </CardHeader>
        <CardContent>
          {loadingUsers ? (
            <p className="text-center py-8 text-slate-500">Loading users...</p>
          ) : fetchError ? (
            <div className="text-center py-8" data-testid="users-error-state">
              <p className="text-red-600 mb-3">{fetchError}</p>
              <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="retry-fetch-users">Retry</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Email/Username</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Certification</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">License #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Account Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{u.first_name} {u.last_name}</td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3 text-slate-600">{u.certification || 'N/A'}</td>
                      <td className="px-4 py-3 text-slate-600">{u.license_number || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getRoleBadge(u.role)}`}>
                          {u.role === 'qa_qi' ? 'QA/QI' : u.role?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash size={16} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>{editingUser ? 'Update user information' : 'Create a new system user'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">First Name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required className="rounded-sm" data-testid="user-firstname-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last Name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required className="rounded-sm" data-testid="user-lastname-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email/Username</Label>
              <Input type="text" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="rounded-sm" data-testid="user-email-input" />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required className="rounded-sm" data-testid="user-password-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Certification *</Label>
              <Select value={form.certification} onValueChange={(v) => setForm({...form, certification: v, license_number: ''})}>
                <SelectTrigger className="rounded-sm" data-testid="user-certification-select"><SelectValue placeholder="Select certification" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMT">EMT</SelectItem>
                  <SelectItem value="Paramedic">Paramedic</SelectItem>
                  <SelectItem value="Nurse">Nurse</SelectItem>
                  <SelectItem value="PA">PA</SelectItem>
                  <SelectItem value="Doctor">Doctor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.certification && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{getLicenseLabel(form.certification)}</Label>
                <Input value={form.license_number} onChange={(e) => setForm({...form, license_number: e.target.value})} placeholder={getLicensePlaceholder(form.certification)} required className="rounded-sm" data-testid="user-license-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Account Type</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue placeholder="Select account type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">ADMIN</SelectItem>
                  <SelectItem value="provider">PROVIDER</SelectItem>
                  <SelectItem value="qa_qi">QA/QI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-button">{editingUser ? 'Update User' : 'Create User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
